import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { HOSPITAL, NAV } from "../data/site";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-brand-beige shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src={HOSPITAL.logo}
            alt="Family HealthCare logo"
            className="h-12 w-12 object-contain"
          />
          <div className="leading-tight">
            <div className="font-serif-display text-xl text-brand-ink">
              Family <span className="text-brand-maroon">HealthCare</span>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-brand-maroon/80 font-semibold">
              Hospital • Gurugram
            </div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  isActive
                    ? "text-brand-maroon bg-brand-maroon/8"
                    : "text-brand-ink/75 hover:text-brand-maroon hover:bg-brand-maroon/5"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={`tel:${HOSPITAL.phoneDial}`}
            data-testid="header-call-btn"
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-brand-maroon text-white text-sm font-semibold px-5 py-2.5 hover:bg-brand-maroonDark transition"
          >
            <Phone size={15} />
            {HOSPITAL.phone}
          </a>

          <button
            type="button"
            data-testid="header-menu-toggle"
            aria-label="Open menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 rounded-full hover:bg-brand-beige"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          className="lg:hidden border-t border-brand-beige bg-white"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base ${
                    isActive
                      ? "bg-brand-maroon/10 text-brand-maroon font-semibold"
                      : "text-brand-ink/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <a
              href={`tel:${HOSPITAL.phoneDial}`}
              data-testid="mobile-call-btn"
              className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-brand-maroon text-white text-sm font-semibold px-5 py-3"
            >
              <Phone size={15} />
              {HOSPITAL.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
